/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */
import React, { useState, useEffect, useCallback } from 'react';
import EnvelopesIcon from '../../../images/envelopes.svg';
import CONFIG from '../../../config';
import { theme } from './theme';
import { useAppSelector } from '../../../reducers';
import MagicBell, { NotificationList, useNotifications } from '@magicbell/magicbell-react';
import G17Client from '../../../services/G17Client';
import { Button, Input, FormGroup, Label } from 'reactstrap';
import { capitaliseFirstLetter, naturalSort, replaceUnderscores } from '../../../utils';
import Loader from '../../loader';
import { BasicAlert } from '../../alert/BasicAlert';
import { NotificationPreferencesUpdateRequest, NotificationServices, NotificationChannels } from '../../../types/notifications';
import { deleteAllNotifications } from './magicbell-notifications';
import { Checkbox, CheckboxState } from '../../common/Checkbox';
import './styles.scss';

interface NotificationsWidgetProps {
  launcherRef: React.RefObject<Element>;
  isOpen: boolean;
  toggle: () => void;
  userKey: string;
  userExternalId: string | undefined;
}

type NotificationStore = ReturnType<typeof useNotifications>;

interface NotificationsInboxProps {
  store: NotificationStore | null;
}

const NotificationsInbox = (props: NotificationsInboxProps) => {
  if (props.store?.notifications && props.store.notifications.length === 0) {
    return (
      <div className='notifications-inbox h-100 d-flex flex-column justify-content-center text-center'>
        <div>
          <img src={EnvelopesIcon} alt='No notifications' />
        </div>
        <div className='mt-3'>
          You have no recent notifications
        </div>
      </div>
    );
  }

  return (
    <div className='notifications-inbox'>
      {props.store ? <NotificationList notifications={props.store} /> : <>Unable to get notifications</>}
    </div>
  );
}

interface NotificationPreferencesCategory {
  code: string;
  label: string;
  in_app: boolean;
  email: boolean;
  group: string;
}

interface CategoryGroup {
  group: string;
  channel: NotificationChannels;
}

const NotificationsPreferences = () => {

  const [categories, setCategories] = useState<NotificationPreferencesCategory[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const reload = () => {
    setCategories([]);
    setErrorMessage('');
    G17Client.getNotificationPreferences()
      .then((preferences) => {
        if (preferences.magicBell.notification_preferences) {
          const categoryPreferences = preferences.magicBell.notification_preferences.categories;
          const groups = preferences.magicBell.notification_preferences.groups;
          const categories = groups
            .map((group) =>
              Object.keys(categoryPreferences)
                .filter((category) => categoryPreferences[category].group === group)
                .sort(naturalSort)
                .map((category) => ({
                  code: category,
                  label: capitaliseFirstLetter(replaceUnderscores(category)),
                  in_app: categoryPreferences[category].in_app ?? false,
                  email: categoryPreferences[category].email ?? false,
                  group: categoryPreferences[category].group,
                }))
            )
            .flat();
          setCategories(categories);
        }
      })
      .catch((e) => setErrorMessage(e.message));
  };

  const togglePreference = (category: string | string[], channel: NotificationChannels, setting: boolean) => {
    setCategories([]);
    setErrorMessage('');
    const preferences: NotificationPreferencesUpdateRequest = {
      service: NotificationServices.MAGIC_BELL,
      category,
      channel,
      setting,
    };

    G17Client.setNotificationPreference(preferences)
      .then(() => reload())
      .catch((e) => setErrorMessage(e.message));
  };

  const toggleGroup = ({ group, channel }: CategoryGroup) => {
    const status = getGroupStatus({ group, channel });

    const categories = getCategoriesByGroup(group).map((category) => category.code);
    togglePreference(categories, channel, status !== CheckboxState.Checked);
  };

  const getCategoriesByGroup = (group: string) => categories.filter((category) => category.group === group);

  const isGroupChecked = ({ group, channel }: CategoryGroup) =>
    getCategoriesByGroup(group).every((category) => category[channel]);

  const isGroupUnchecked = ({ group, channel }: CategoryGroup) =>
    getCategoriesByGroup(group).every((category) => !category[channel]);

  const getGroupStatus = ({ group, channel }: CategoryGroup) =>
    isGroupChecked({ group, channel })
      ? CheckboxState.Checked
      : isGroupUnchecked({ group, channel })
      ? CheckboxState.Unchecked
      : CheckboxState.Indeterminate;

  React.useEffect(() => {
    reload();
  }, []);

  if (errorMessage) {
    return (
      <div className='notifications-preferences'>
        <BasicAlert type={'danger'}>{errorMessage}</BasicAlert>
      </div>
    )
  }

  if (!categories || categories.length === 0) {
    return <Loader />;
  }

  const groups = categories.reduce(
    (prev, curr) => (prev.includes(curr.group) ? prev : [...prev, curr.group]),
    [] as string[]
  );

  return (
    <div className='notifications-preferences'>
      <table width='100%'>
        <thead>
          <tr>
            <th />
            <th className='text-strong text-ThemeTextDark'>PLATFORM</th>
            <th className='text-strong text-ThemeTextDark'>E-MAIL</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <React.Fragment key={group}>
              <tr className='notifications-preferences__group'>
                <td className='text-left text-strong text-ThemeTextDark'>
                  <Label>{capitaliseFirstLetter(replaceUnderscores(group))}</Label>
                </td>
                <td>
                  <FormGroup check inline className='mx-0'>
                    <Checkbox
                      status={getGroupStatus({ group, channel: NotificationChannels.IN_APP })}
                      code={group}
                      onChange={() => toggleGroup({ group, channel: NotificationChannels.IN_APP })}
                    />
                  </FormGroup>
                </td>
                <td>
                  <FormGroup check inline className='mx-0'>
                    <Checkbox
                      status={getGroupStatus({ group, channel: NotificationChannels.EMAIL })}
                      code={group}
                      onChange={() => toggleGroup({ group, channel: NotificationChannels.EMAIL })}
                    />
                  </FormGroup>
                </td>
              </tr>
              {getCategoriesByGroup(group).map((category) => (
                <tr key={category.code}>
                  <td className='text-left text-ThemeTextDark'>
                    <Label>{category.label}</Label>
                  </td>
                  <td>
                    <FormGroup check inline className='mx-0'>
                      <Input
                        type='checkbox'
                        code={category.code}
                        checked={category.in_app}
                        onChange={() => togglePreference(category.code, NotificationChannels.IN_APP, !category.in_app)}
                      />
                    </FormGroup>
                  </td>
                  <td>
                    <FormGroup check inline className='mx-0'>
                      <Input
                        type='checkbox'
                        code={category.code}
                        checked={category.email}
                        onChange={() => togglePreference(category.code, NotificationChannels.EMAIL, !category.email)}
                      />
                    </FormGroup>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const NotificationsWidget = (props: NotificationsWidgetProps) => {

  const [showPreferences, setShowPreferences] = useState(false);
  const store = useNotifications();
  const markAsRead = () => store?.markAllAsRead();
  const togglePreferences = () => setShowPreferences(!showPreferences);
  const deleteAll = useCallback(async () => await deleteAllNotifications(props.userKey, props.userExternalId), [props]);

  return (
    <>
      <div className='notifications-arrow'></div>
      <div className='notifications-background' onClick={() => props.toggle()}></div>
      <div className='notifications-container'>
        <div className='notifications-header d-flex align-items-center'>
          <div className='h5 text-ThemeTextWhite flex-fill'>{showPreferences ? 'PREFERENCES' : 'NOTIFICATIONS'}</div>
          <div>
            {showPreferences
              ?
              <Button color='link' className='p-0' onClick={togglePreferences}><i className='fas fa-times' /></Button>
              :
              <>
                <Button color='link' className='p-0 mr-3' onClick={deleteAll}>Delete All</Button>
                <Button color='link' className='p-0' onClick={markAsRead}>Mark All Read</Button>
              </>
            }
          </div>
        </div>
        <div className='notifications-body'>
          {showPreferences ? <NotificationsPreferences /> : <NotificationsInbox store={store} />}
        </div>
        <div className='notifications-footer'>
          <Button color='link' size='lg' onClick={togglePreferences}>
            <i className='text-ThemeTextWhite fal fa-cog' />
          </Button>
        </div>
      </div>
    </>
  );
}

const Notifications = () => {
  const currentUser = useAppSelector((state) => state.currentUser);
  const loggedIn = currentUser.loaded;
  const [authCode, setAuthCode] = useState('');

  useEffect(() => {
    if (loggedIn) {
      G17Client.getMagicBellToken().then((authCode) => setAuthCode(authCode))
    }
  }, [loggedIn]);

  if (!authCode) {
    return null;
  }

  if (!currentUser.data.email) {
    return null;
  }

  return <MagicBell
    apiKey={CONFIG.magicBellApiKey}
    userExternalId={currentUser.data._id}
    userKey={authCode}
    theme={theme}
    images={{
      emptyInboxUrl: EnvelopesIcon
    }}
    userEmail={currentUser.data.email}
    locale={{
      name: 'en-US',
      translations: {
        messages: {
          'empty-inbox': 'You have no new notifications.',
        },
      },
    }}
  >
    {
      (props) => <NotificationsWidget {...props} userKey={authCode} userExternalId={currentUser.data._id} />
    }
  </MagicBell>
};

export default Notifications;
